import React, { useState, useEffect, useRef, useCallback } from 'react';
import { ClipboardList, CheckCircle, XCircle, Search, Package, AlertTriangle, BarChart3, Scan } from 'lucide-react';
import { API_URL } from '../../services/api';

const Inventaire = () => {
  // --- State ---
  const [view, setView] = useState('list'); // list | create | counting | detail
  const [inventaires, setInventaires] = useState([]);
  const [boutiques, setBoutiques] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedInventaire, setSelectedInventaire] = useState(null);
  const [lignes, setLignes] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [ajusterStock, setAjusterStock] = useState(false);

  // Filters
  const [filterBoutique, setFilterBoutique] = useState('');
  const [filterStatut, setFilterStatut] = useState('');
  const [filterDateDebut, setFilterDateDebut] = useState('');
  const [filterDateFin, setFilterDateFin] = useState('');

  // Create form
  const [formBoutique, setFormBoutique] = useState('');
  const [formType, setFormType] = useState('complet');
  const [formCommentaire, setFormCommentaire] = useState('');

  // Scan / sélecteur
  const [scanInput, setScanInput] = useState('');
  const [scanResult, setScanResult] = useState(null);
  const [scanSuggestions, setScanSuggestions] = useState([]);
  const [ligneSelectionnee, setLigneSelectionnee] = useState(null);
  const scanRef = useRef(null);
  const rowRefs = useRef({});
  const qtyRefs = useRef({});

  // Debounce timers
  const debounceTimers = React.useRef({});

  // --- Load data ---
  useEffect(() => {
    loadInventaires();
    loadBoutiques();
  }, []);

  const loadBoutiques = async () => {
    try {
      const response = await fetch(`${API_URL}/boutiques`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      const data = await response.json();
      setBoutiques(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Erreur chargement boutiques:', error);
    }
  };

  const loadInventaires = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (filterBoutique) params.append('boutique_id', filterBoutique);
      if (filterStatut) params.append('statut', filterStatut);
      if (filterDateDebut) params.append('date_debut', filterDateDebut);
      if (filterDateFin) params.append('date_fin', filterDateFin);
      const query = params.toString() ? `?${params.toString()}` : '';
      const response = await fetch(`${API_URL}/inventaires${query}`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      const data = await response.json();
      setInventaires(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Erreur chargement inventaires:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadInventaires();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterBoutique, filterStatut, filterDateDebut, filterDateFin]);

  const loadLignes = async (inventaireId) => {
    try {
      const response = await fetch(`${API_URL}/inventaires/${inventaireId}`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      const data = await response.json();
      setSelectedInventaire(data);
      setLignes(Array.isArray(data.lignes) ? data.lignes : []);
    } catch (error) {
      console.error('Erreur chargement lignes inventaire:', error);
    }
  };

  // --- Actions ---
  const handleCreate = async (e) => {
    e.preventDefault();
    if (!formBoutique) { alert('Veuillez choisir une boutique'); return; }
    try {
      const response = await fetch(`${API_URL}/inventaires`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          boutique_id: parseInt(formBoutique),
          type: formType,
          commentaire: formCommentaire
        })
      });
      if (!response.ok) {
        const err = await response.json();
        alert(err.error || 'Erreur lors de la creation');
        return;
      }
      const newInv = await response.json();
      const invId = newInv.id || newInv.inventaire_id;
      await loadLignes(invId);
      setView('counting');
      setFormBoutique('');
      setFormType('complet');
      setFormCommentaire('');
      loadInventaires();
    } catch (error) {
      console.error('Erreur creation inventaire:', error);
      alert('Erreur lors de la creation de l\'inventaire');
    }
  };

  const handleUpdateLigne = (ligneId, field, value) => {
    setLignes(prev => prev.map(l => {
      if (l.id === ligneId) {
        const updated = { ...l, [field]: value };
        if (field === 'quantite_comptee') {
          updated.ecart = (parseFloat(value) || 0) - (parseFloat(l.stock_theorique) || 0);
        }
        return updated;
      }
      return l;
    }));

    // Debounce API call
    if (debounceTimers.current[ligneId]) {
      clearTimeout(debounceTimers.current[ligneId]);
    }
    debounceTimers.current[ligneId] = setTimeout(async () => {
      try {
        const ligne = lignes.find(l => l.id === ligneId);
        const payload = { [field]: value };
        if (field === 'quantite_comptee') {
          payload.quantite_comptee = parseFloat(value) || 0;
        }
        await fetch(`${API_URL}/inventaires/${selectedInventaire.id}/ligne/${ligneId}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          body: JSON.stringify(payload)
        });
      } catch (error) {
        console.error('Erreur mise a jour ligne:', error);
      }
    }, 500);
  };

  const handleValider = async () => {
    try {
      const response = await fetch(
        `${API_URL}/inventaires/${selectedInventaire.id}/valider?ajuster_stock=${ajusterStock}`,
        {
          method: 'PUT',
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        }
      );
      if (!response.ok) {
        const err = await response.json();
        alert(err.error || 'Erreur lors de la validation');
        return;
      }
      setShowConfirmation(false);
      setView('list');
      loadInventaires();
      alert('Inventaire valide avec succes');
    } catch (error) {
      console.error('Erreur validation:', error);
      alert('Erreur lors de la validation');
    }
  };

  const openCounting = async (inv) => {
    await loadLignes(inv.id);
    setView('counting');
  };

  const openDetail = async (inv) => {
    await loadLignes(inv.id);
    setView('detail');
  };

  const handleScanInput = useCallback((value) => {
    setScanInput(value);
    const term = value.trim().toLowerCase();
    if (term.length < 2) {
      setScanSuggestions([]);
      return;
    }
    const matches = lignes.filter(l =>
      (l.reference || '').toLowerCase().includes(term) ||
      (l.designation || '').toLowerCase().includes(term)
    );
    setScanSuggestions(matches.slice(0, 8));
  }, [lignes]);

  const handleSelectLigne = useCallback((ligne) => {
    setLigneSelectionnee(ligne);
    setScanInput('');
    setScanSuggestions([]);
    setSearchTerm('');
    // Scroll + highlight
    if (rowRefs.current[ligne.id]) {
      rowRefs.current[ligne.id].scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
    // Focus quantité
    setTimeout(() => {
      if (qtyRefs.current[ligne.id]) qtyRefs.current[ligne.id].focus();
    }, 300);
  }, []);

  const handleScan = useCallback((ref) => {
    const term = ref.trim().toLowerCase();
    if (!term) return;
    // Correspondance exacte d'abord, sinon première partielle
    const ligne = lignes.find(l => (l.reference || '').toLowerCase() === term)
      || lignes.find(l => (l.reference || '').toLowerCase().includes(term));
    if (ligne) {
      handleSelectLigne(ligne);
    } else {
      setScanResult({ found: false, message: `Référence "${ref}" introuvable` });
      setTimeout(() => setScanResult(null), 3000);
    }
    setScanInput('');
    setScanSuggestions([]);
  }, [lignes, handleSelectLigne]);

  // --- Computed values ---
  const getBoutiqueName = (id) => {
    const b = boutiques.find(b => b.id === parseInt(id));
    return b ? b.nom : `Boutique #${id}`;
  };

  const getStatutBadge = (statut) => {
    const colors = {
      en_cours: 'bg-yellow-100 text-yellow-800',
      valide: 'bg-green-100 text-green-800',
      annule: 'bg-red-100 text-red-800',
      brouillon: 'bg-gray-100 text-gray-800'
    };
    const labels = {
      en_cours: 'En cours',
      valide: 'Valide',
      annule: 'Annule',
      brouillon: 'Brouillon'
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-semibold ${colors[statut] || 'bg-gray-100 text-gray-800'}`}>
        {labels[statut] || statut}
      </span>
    );
  };

  const stats = {
    enCours: inventaires.filter(i => i.statut === 'en_cours').length,
    validesCeMois: inventaires.filter(i => {
      if (i.statut !== 'valide') return false;
      const now = new Date();
      const d = new Date(i.date_validation || i.date_creation);
      return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
    }).length,
    ecartTotal: inventaires.reduce((sum, i) => sum + (parseFloat(i.ecart_valeur) || 0), 0)
  };

  const filteredLignes = lignes.filter(l => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return (
      (l.reference || '').toLowerCase().includes(term) ||
      (l.designation || '').toLowerCase().includes(term)
    );
  });

  const countingStats = {
    total: lignes.length,
    comptees: lignes.filter(l => l.quantite_comptee !== null && l.quantite_comptee !== undefined && l.quantite_comptee !== '').length,
    totalTheorique: lignes.reduce((s, l) => s + (parseFloat(l.stock_theorique) || 0), 0),
    totalCompte: lignes.reduce((s, l) => s + (parseFloat(l.quantite_comptee) || 0), 0),
    avecEcart: lignes.filter(l => {
      const ecart = (parseFloat(l.quantite_comptee) || 0) - (parseFloat(l.stock_theorique) || 0);
      return l.quantite_comptee !== null && l.quantite_comptee !== undefined && l.quantite_comptee !== '' && ecart !== 0;
    }).length,
    valeurEcart: lignes.reduce((s, l) => {
      const ecart = (parseFloat(l.quantite_comptee) || 0) - (parseFloat(l.stock_theorique) || 0);
      return s + ecart * (parseFloat(l.prix_unitaire) || 0);
    }, 0)
  };

  const progressPercent = countingStats.total > 0
    ? Math.round((countingStats.comptees / countingStats.total) * 100)
    : 0;

  // --- Render helpers ---
  const getEcartColor = (ligne) => {
    if (ligne.quantite_comptee === null || ligne.quantite_comptee === undefined || ligne.quantite_comptee === '') {
      return 'text-gray-400';
    }
    const ecart = (parseFloat(ligne.quantite_comptee) || 0) - (parseFloat(ligne.stock_theorique) || 0);
    if (ecart === 0) return 'text-green-600 font-semibold';
    if (ecart < 0) return 'text-red-600 font-bold';
    return 'text-orange-500 font-semibold';
  };

  const getEcartValue = (ligne) => {
    if (ligne.quantite_comptee === null || ligne.quantite_comptee === undefined || ligne.quantite_comptee === '') {
      return '-';
    }
    const ecart = (parseFloat(ligne.quantite_comptee) || 0) - (parseFloat(ligne.stock_theorique) || 0);
    return ecart > 0 ? `+${ecart}` : ecart;
  };

  // ========== VIEW: CREATE ==========
  if (view === 'create') {
    return (
      <div className="space-y-6">
        <div className="flex items-center space-x-4">
          <button onClick={() => setView('list')} className="text-gray-500 hover:text-gray-700 text-2xl">&larr;</button>
          <h2 className="text-3xl font-bold text-gray-800 flex items-center space-x-2">
            <ClipboardList className="h-8 w-8 text-purple-600" />
            <span>Nouvel inventaire</span>
          </h2>
        </div>

        <div className="bg-white rounded-lg shadow-md p-6 max-w-xl">
          <form onSubmit={handleCreate} className="space-y-5">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Boutique *</label>
              <select
                value={formBoutique}
                onChange={e => setFormBoutique(e.target.value)}
                className="w-full border rounded-lg p-2.5 focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                required
              >
                <option value="">-- Choisir une boutique --</option>
                {boutiques.map(b => (
                  <option key={b.id} value={b.id}>{b.nom}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Type d'inventaire</label>
              <div className="flex space-x-4">
                <label className={`flex-1 flex items-center justify-center p-3 rounded-lg border-2 cursor-pointer transition-all ${
                  formType === 'complet' ? 'border-purple-500 bg-purple-50 text-purple-700' : 'border-gray-200 hover:border-gray-300'
                }`}>
                  <input type="radio" name="type" value="complet" checked={formType === 'complet'}
                    onChange={e => setFormType(e.target.value)} className="sr-only" />
                  <Package className="h-5 w-5 mr-2" />
                  <span className="font-medium">Complet</span>
                </label>
                <label className={`flex-1 flex items-center justify-center p-3 rounded-lg border-2 cursor-pointer transition-all ${
                  formType === 'partiel' ? 'border-indigo-500 bg-indigo-50 text-indigo-700' : 'border-gray-200 hover:border-gray-300'
                }`}>
                  <input type="radio" name="type" value="partiel" checked={formType === 'partiel'}
                    onChange={e => setFormType(e.target.value)} className="sr-only" />
                  <ClipboardList className="h-5 w-5 mr-2" />
                  <span className="font-medium">Partiel</span>
                </label>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Commentaire</label>
              <textarea
                value={formCommentaire}
                onChange={e => setFormCommentaire(e.target.value)}
                rows={3}
                className="w-full border rounded-lg p-2.5 focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                placeholder="Remarques sur cet inventaire..."
              />
            </div>

            <div className="flex space-x-3 pt-2">
              <button type="submit"
                className="flex-1 bg-gradient-to-r from-purple-600 to-indigo-600 text-white py-2.5 rounded-lg font-semibold hover:from-purple-700 hover:to-indigo-700 transition-all shadow-md">
                Creer et commencer le comptage
              </button>
              <button type="button" onClick={() => setView('list')}
                className="px-6 py-2.5 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-all">
                Annuler
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  }

  // ========== VIEW: COUNTING / DETAIL ==========
  if (view === 'counting' || view === 'detail') {
    const isReadOnly = view === 'detail' || selectedInventaire?.statut === 'valide';

    return (
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <button onClick={() => { setView('list'); setSearchTerm(''); }}
              className="text-gray-500 hover:text-gray-700 text-2xl">&larr;</button>
            <div>
              <h2 className="text-2xl font-bold text-gray-800 flex items-center space-x-2">
                <ClipboardList className="h-7 w-7 text-purple-600" />
                <span>
                  Inventaire #{selectedInventaire?.id} - {getBoutiqueName(selectedInventaire?.boutique_id)}
                </span>
              </h2>
              <p className="text-sm text-gray-500 mt-1">
                {selectedInventaire?.date_creation && new Date(selectedInventaire.date_creation).toLocaleDateString('fr-FR')}
                {' '}&bull; Type : {selectedInventaire?.type || 'complet'}
                {isReadOnly && selectedInventaire?.date_validation && (
                  <span className="ml-3 text-green-600">
                    Valide le {new Date(selectedInventaire.date_validation).toLocaleDateString('fr-FR')}
                    {selectedInventaire.valide_par && ` par ${selectedInventaire.valide_par}`}
                  </span>
                )}
              </p>
            </div>
          </div>
          {isReadOnly && (
            <span className="px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm font-semibold flex items-center space-x-1">
              <CheckCircle className="h-4 w-4" />
              <span>Valide</span>
            </span>
          )}
        </div>

        {/* Progress bar */}
        <div className="bg-white rounded-lg shadow-md p-4">
          <div className="flex justify-between items-center mb-2">
            <span className="text-sm font-medium text-gray-700">
              Progression : {countingStats.comptees}/{countingStats.total} articles comptes
            </span>
            <span className="text-sm font-bold text-purple-600">{progressPercent}%</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-3">
            <div
              className="bg-gradient-to-r from-purple-500 to-indigo-500 h-3 rounded-full transition-all duration-500"
              style={{ width: `${progressPercent}%` }}
            />
          </div>
        </div>

        {/* Sélecteur bijou — scan ou recherche */}
        {!isReadOnly && (
          <div className="bg-purple-50 rounded-lg border border-purple-200 p-4">
            <h4 className="font-bold text-purple-900 mb-3 flex items-center gap-2">
              <Scan className="h-5 w-5" />
              Scanner ou rechercher un article
            </h4>
            <input
              ref={scanRef}
              type="text"
              value={scanInput}
              onChange={e => handleScanInput(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') handleScan(scanInput); }}
              placeholder="Tapez une référence, désignation... ou scannez le code-barres"
              className="w-full px-4 py-3 border-2 border-purple-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 text-base font-mono"
              autoFocus
            />

            {/* Cartes de suggestions */}
            {scanSuggestions.length > 0 && (
              <div className="space-y-2 mt-3 max-h-64 overflow-y-auto">
                {scanSuggestions.map(ligne => (
                  <div
                    key={ligne.id}
                    onClick={() => handleSelectLigne(ligne)}
                    className={`p-3 rounded-lg border-2 cursor-pointer transition-all ${
                      ligneSelectionnee?.id === ligne.id
                        ? 'bg-purple-100 border-purple-500'
                        : 'bg-white border-gray-200 hover:border-purple-400 hover:bg-purple-50'
                    }`}
                  >
                    <div className="flex justify-between items-center">
                      <div>
                        <div className="font-bold text-purple-900 font-mono">{ligne.reference || '—'}</div>
                        <div className="text-sm text-gray-700">{ligne.designation || ''}</div>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="text-right">
                          <div className="text-xs text-gray-500">Théorique</div>
                          <div className="font-semibold text-gray-800">{ligne.stock_theorique ?? '—'}</div>
                        </div>
                        {ligne.quantite_comptee !== null && ligne.quantite_comptee !== undefined && ligne.quantite_comptee !== '' && (
                          <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0" />
                        )}
                        {ligneSelectionnee?.id === ligne.id && (
                          <div className="w-2 h-2 rounded-full bg-purple-600 flex-shrink-0" />
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {scanInput.length >= 2 && scanSuggestions.length === 0 && (
              <div className="mt-2 px-3 py-2 bg-red-50 text-red-700 border border-red-200 rounded-lg text-sm">
                Aucun article trouvé pour "{scanInput}"
              </div>
            )}

            {scanResult && !scanResult.found && (
              <div className="mt-2 px-3 py-2 bg-red-50 text-red-700 border border-red-200 rounded-lg text-sm">
                {scanResult.message}
              </div>
            )}

            {ligneSelectionnee && (
              <div className="mt-2 px-3 py-2 bg-green-50 text-green-700 border border-green-200 rounded-lg text-sm font-medium">
                ✓ {ligneSelectionnee.reference} sélectionné — saisissez la quantité comptée dans le tableau
              </div>
            )}
          </div>
        )}

        {/* Search filtre */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
          <input
            type="text"
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            placeholder="Filtrer par reference ou designation..."
            className="w-full pl-10 pr-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
          />
        </div>

        {/* Table */}
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gradient-to-r from-purple-50 to-indigo-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Reference</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Designation</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Stock theorique</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Qte comptee</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Ecart</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Note</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filteredLignes.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="px-4 py-8 text-center text-gray-400">
                      {searchTerm ? 'Aucun article correspondant' : 'Aucune ligne dans cet inventaire'}
                    </td>
                  </tr>
                ) : (
                  filteredLignes.map(ligne => (
                    <tr
                      key={ligne.id}
                      ref={el => rowRefs.current[ligne.id] = el}
                      className={`transition-colors ${
                        ligneSelectionnee?.id === ligne.id
                          ? 'bg-purple-50 border-l-4 border-purple-500'
                          : 'hover:bg-gray-50'
                      }`}
                    >
                      <td className="px-4 py-2.5 text-sm font-mono text-purple-700">{ligne.reference || '-'}</td>
                      <td className="px-4 py-2.5 text-sm text-gray-800">{ligne.designation || '-'}</td>
                      <td className="px-4 py-2.5 text-sm text-center font-medium">{ligne.stock_theorique ?? '-'}</td>
                      <td className="px-4 py-2.5 text-center">
                        {isReadOnly ? (
                          <span className="text-sm font-medium">{ligne.quantite_comptee ?? '-'}</span>
                        ) : (
                          <input
                            ref={el => qtyRefs.current[ligne.id] = el}
                            type="text" inputMode="decimal" onFocus={e => e.target.select()}
                            min="0"
                            value={ligne.quantite_comptee ?? ''}
                            onChange={e => handleUpdateLigne(ligne.id, 'quantite_comptee', e.target.value)}
                            onKeyDown={e => { if (e.key === 'Enter') { setLigneSelectionnee(null); if (scanRef.current) scanRef.current.focus(); } }}
                            className="w-20 text-center border rounded-lg p-1.5 text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                            placeholder="..."
                          />
                        )}
                      </td>
                      <td className={`px-4 py-2.5 text-sm text-center ${getEcartColor(ligne)}`}>
                        {getEcartValue(ligne)}
                      </td>
                      <td className="px-4 py-2.5">
                        {isReadOnly ? (
                          <span className="text-sm text-gray-500">{ligne.note || ''}</span>
                        ) : (
                          <input
                            type="text"
                            value={ligne.note || ''}
                            onChange={e => handleUpdateLigne(ligne.id, 'note', e.target.value)}
                            className="w-full border rounded-lg p-1.5 text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                            placeholder="Note..."
                          />
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Summary panel */}
        <div className="bg-white rounded-lg shadow-md p-5">
          <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center space-x-2">
            <BarChart3 className="h-5 w-5 text-indigo-600" />
            <span>Resume de l'inventaire</span>
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
            <div className="bg-purple-50 rounded-lg p-3 text-center">
              <p className="text-2xl font-bold text-purple-700">{countingStats.totalTheorique}</p>
              <p className="text-xs text-gray-500 mt-1">Total theorique</p>
            </div>
            <div className="bg-indigo-50 rounded-lg p-3 text-center">
              <p className="text-2xl font-bold text-indigo-700">{countingStats.totalCompte}</p>
              <p className="text-xs text-gray-500 mt-1">Total compte</p>
            </div>
            <div className="bg-orange-50 rounded-lg p-3 text-center">
              <p className="text-2xl font-bold text-orange-600">{countingStats.avecEcart}</p>
              <p className="text-xs text-gray-500 mt-1">Articles avec ecart</p>
            </div>
            <div className={`rounded-lg p-3 text-center ${countingStats.valeurEcart < 0 ? 'bg-red-50' : countingStats.valeurEcart > 0 ? 'bg-orange-50' : 'bg-green-50'}`}>
              <p className={`text-2xl font-bold ${countingStats.valeurEcart < 0 ? 'text-red-600' : countingStats.valeurEcart > 0 ? 'text-orange-600' : 'text-green-600'}`}>
                {countingStats.valeurEcart >= 0 ? '+' : ''}{countingStats.valeurEcart.toFixed(2)} DH
              </p>
              <p className="text-xs text-gray-500 mt-1">Valeur ecart total</p>
            </div>
          </div>

          {/* Validation controls */}
          {!isReadOnly && (
            <div className="border-t pt-4 mt-4">
              <div className="flex items-center justify-between">
                <label className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={ajusterStock}
                    onChange={e => setAjusterStock(e.target.checked)}
                    className="w-4 h-4 text-purple-600 rounded focus:ring-purple-500"
                  />
                  <span className="text-sm text-gray-700">Ajuster le stock automatiquement apres validation</span>
                </label>
                <button
                  onClick={() => setShowConfirmation(true)}
                  className="bg-gradient-to-r from-green-500 to-emerald-600 text-white px-6 py-2.5 rounded-lg font-semibold hover:from-green-600 hover:to-emerald-700 transition-all shadow-md flex items-center space-x-2"
                >
                  <CheckCircle className="h-5 w-5" />
                  <span>Valider l'inventaire</span>
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Confirmation dialog */}
        {showConfirmation && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-end sm:items-center justify-center z-50">
            <div className="bg-white rounded-t-2xl sm:rounded-xl shadow-2xl p-6 max-w-md w-full mx-0 sm:mx-4 max-h-[95vh] sm:max-h-[90vh] overflow-y-auto">
              <div className="flex items-center space-x-3 mb-4">
                <AlertTriangle className="h-8 w-8 text-orange-500" />
                <h3 className="text-xl font-bold text-gray-800">Confirmer la validation</h3>
              </div>
              <div className="space-y-3 mb-6">
                <p className="text-gray-600">
                  Vous etes sur le point de valider cet inventaire avec les resultats suivants :
                </p>
                <ul className="text-sm text-gray-700 space-y-1 bg-gray-50 rounded-lg p-3">
                  <li><strong>{countingStats.comptees}</strong> articles comptes sur <strong>{countingStats.total}</strong></li>
                  <li><strong>{countingStats.avecEcart}</strong> articles avec ecart</li>
                  <li>Valeur ecart : <strong className={countingStats.valeurEcart < 0 ? 'text-red-600' : 'text-orange-600'}>
                    {countingStats.valeurEcart.toFixed(2)} DH
                  </strong></li>
                </ul>
                {ajusterStock && (
                  <div className="flex items-center space-x-2 bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                    <AlertTriangle className="h-5 w-5 text-yellow-600 flex-shrink-0" />
                    <p className="text-sm text-yellow-800">
                      Le stock sera ajuste automatiquement selon les quantites comptees.
                    </p>
                  </div>
                )}
              </div>
              <div className="flex space-x-3">
                <button onClick={handleValider}
                  className="flex-1 bg-gradient-to-r from-green-500 to-emerald-600 text-white py-2.5 rounded-lg font-semibold hover:from-green-600 hover:to-emerald-700 transition-all">
                  Confirmer la validation
                </button>
                <button onClick={() => setShowConfirmation(false)}
                  className="flex-1 border border-gray-300 text-gray-700 py-2.5 rounded-lg font-semibold hover:bg-gray-50 transition-all">
                  Annuler
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // ========== VIEW: LIST (default) ==========
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
        <h2 className="text-3xl font-bold text-gray-800 flex items-center space-x-2">
          <ClipboardList className="h-8 w-8 text-purple-600" />
          <span>Inventaires physiques</span>
        </h2>
        <button
          onClick={() => setView('create')}
          className="w-full sm:w-auto bg-gradient-to-r from-purple-600 to-indigo-600 text-white px-5 py-2.5 rounded-lg font-semibold hover:from-purple-700 hover:to-indigo-700 transition-all shadow-md flex items-center justify-center space-x-2"
        >
          <ClipboardList className="h-5 w-5" />
          <span>Nouvel inventaire</span>
        </button>
      </div>

      {/* Stats cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
        <div className="bg-gradient-to-br from-purple-500 to-indigo-600 rounded-xl shadow-lg p-5 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm opacity-80">Inventaires en cours</p>
              <p className="text-3xl font-bold mt-1">{stats.enCours}</p>
            </div>
            <Package className="h-10 w-10 opacity-30" />
          </div>
        </div>
        <div className="bg-gradient-to-br from-green-500 to-emerald-600 rounded-xl shadow-lg p-5 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm opacity-80">Valides ce mois</p>
              <p className="text-3xl font-bold mt-1">{stats.validesCeMois}</p>
            </div>
            <CheckCircle className="h-10 w-10 opacity-30" />
          </div>
        </div>
        <div className={`bg-gradient-to-br ${stats.ecartTotal < 0 ? 'from-red-500 to-rose-600' : 'from-orange-500 to-amber-600'} rounded-xl shadow-lg p-5 text-white`}>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm opacity-80">Ecart total</p>
              <p className="text-3xl font-bold mt-1">{stats.ecartTotal.toFixed(2)} DH</p>
            </div>
            <BarChart3 className="h-10 w-10 opacity-30" />
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-md p-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <select value={filterBoutique} onChange={e => setFilterBoutique(e.target.value)}
            className="border rounded-lg p-2.5 focus:ring-2 focus:ring-purple-500 focus:border-purple-500">
            <option value="">Toutes les boutiques</option>
            {boutiques.map(b => (
              <option key={b.id} value={b.id}>{b.nom}</option>
            ))}
          </select>
          <select value={filterStatut} onChange={e => setFilterStatut(e.target.value)}
            className="border rounded-lg p-2.5 focus:ring-2 focus:ring-purple-500 focus:border-purple-500">
            <option value="">Tous les statuts</option>
            <option value="en_cours">En cours</option>
            <option value="valide">Valide</option>
            <option value="annule">Annule</option>
          </select>
          <input type="date" value={filterDateDebut} onChange={e => setFilterDateDebut(e.target.value)}
            className="border rounded-lg p-2.5 focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
            placeholder="Date debut" />
          <input type="date" value={filterDateFin} onChange={e => setFilterDateFin(e.target.value)}
            className="border rounded-lg p-2.5 focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
            placeholder="Date fin" />
        </div>
      </div>

      {/* Mobile cards — inventaires */}
      {!loading && inventaires.length > 0 && (
        <div className="md:hidden space-y-3">
          {inventaires.map(inv => (
            <div key={inv.id} className="bg-white rounded-lg shadow p-4 border-l-4 border-purple-400">
              <div className="flex justify-between items-start mb-2">
                <div>
                  <div className="font-bold text-gray-900">{inv.boutique_nom || getBoutiqueName(inv.boutique_id)}</div>
                  <div className="text-xs text-gray-500 capitalize">{inv.type || 'complet'} • {inv.date_creation ? new Date(inv.date_creation).toLocaleDateString('fr-FR') : '-'}</div>
                </div>
                {getStatutBadge(inv.statut)}
              </div>
              <div className="flex justify-between items-center mt-2">
                <div className="text-sm text-gray-600">
                  <span className="font-medium">{inv.articles_comptes ?? 0}</span>
                  <span className="text-gray-400">/{inv.articles_total ?? 0}</span>
                  {inv.ecart_valeur != null && (
                    <span className={`ml-2 font-bold ${(parseFloat(inv.ecart_valeur) || 0) < 0 ? 'text-red-600' : (parseFloat(inv.ecart_valeur) || 0) > 0 ? 'text-orange-500' : 'text-green-600'}`}>
                      {parseFloat(inv.ecart_valeur).toFixed(2)} DH
                    </span>
                  )}
                </div>
                {inv.statut === 'en_cours' ? (
                  <button onClick={() => openCounting(inv)} className="px-3 py-1.5 bg-purple-600 text-white rounded-lg text-sm font-medium">Continuer</button>
                ) : (
                  <button onClick={() => openDetail(inv)} className="px-3 py-1.5 bg-indigo-600 text-white rounded-lg text-sm font-medium">Voir</button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Table */}
      <div className="hidden md:block bg-white rounded-lg shadow-md overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-gray-400">Chargement...</div>
        ) : inventaires.length === 0 ? (
          <div className="p-8 text-center text-gray-400">
            <ClipboardList className="h-12 w-12 mx-auto mb-3 opacity-30" />
            <p>Aucun inventaire trouve</p>
          </div>
        ) : (
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gradient-to-r from-purple-50 to-indigo-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Boutique</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Articles</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Ecart valeur</th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Statut</th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {inventaires.map(inv => (
                <tr key={inv.id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-6 py-3 text-sm text-gray-700">
                    {inv.date_creation ? new Date(inv.date_creation).toLocaleDateString('fr-FR') : '-'}
                  </td>
                  <td className="px-6 py-3 text-sm font-medium text-gray-800">
                    {inv.boutique_nom || getBoutiqueName(inv.boutique_id)}
                  </td>
                  <td className="px-6 py-3 text-sm text-gray-600 capitalize">{inv.type || 'complet'}</td>
                  <td className="px-6 py-3 text-sm text-center">
                    <span className="font-medium">{inv.articles_comptes ?? 0}</span>
                    <span className="text-gray-400">/{inv.articles_total ?? 0}</span>
                  </td>
                  <td className={`px-6 py-3 text-sm text-right font-medium ${
                    (parseFloat(inv.ecart_valeur) || 0) < 0 ? 'text-red-600' :
                    (parseFloat(inv.ecart_valeur) || 0) > 0 ? 'text-orange-500' : 'text-green-600'
                  }`}>
                    {inv.ecart_valeur != null ? `${parseFloat(inv.ecart_valeur).toFixed(2)} DH` : '-'}
                  </td>
                  <td className="px-6 py-3 text-center">{getStatutBadge(inv.statut)}</td>
                  <td className="px-6 py-3 text-center">
                    <div className="flex items-center justify-center space-x-2">
                      {inv.statut === 'en_cours' ? (
                        <button
                          onClick={() => openCounting(inv)}
                          className="text-purple-600 hover:text-purple-800 font-medium text-sm bg-purple-50 hover:bg-purple-100 px-3 py-1 rounded-lg transition-all"
                        >
                          Continuer
                        </button>
                      ) : (
                        <button
                          onClick={() => openDetail(inv)}
                          className="text-indigo-600 hover:text-indigo-800 font-medium text-sm bg-indigo-50 hover:bg-indigo-100 px-3 py-1 rounded-lg transition-all"
                        >
                          Voir
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default Inventaire;
